import { ApiTerrainService } from './../../predictor/services/api-terrain.service';
import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import { SimulationTrackerActions } from './simulation-tracker.actions';
import { append, patch, updateItem, removeItem } from '@ngxs/store/operators';
import { SimulationTracker } from '../../predictor/interfaces/simulation-tracker';
import { SimulationTrackerService } from '../../predictor/services/simulation-tracker.service';
import { asapScheduler, catchError, EMPTY, forkJoin, tap } from 'rxjs';
import { NotificationsActions } from '../notifications/notifications.actions';
import { Severity } from '../../global/enums/severity';
import { FavoritedSimulationTrackers } from 'src/app/predictor/interfaces/favorited-simulation-trackers';
import { SimulationTrackerStatus } from 'src/app/predictor/enums/simulation-tracker-status';
import { SimulationTrackerAgentSettings } from '../../predictor/interfaces/simulation-tracker-agent-settings';
import { AgentSettings } from '../../predictor/interfaces/agent-settings';
import { SimulationRunGroupRequest } from '../../predictor/interfaces/scenario-batch-simulation-request';

export interface SimulationTrackerStateModel {
  simulationTrackers: SimulationTracker[];
  sessionSimulationTrackers: SimulationTracker[];
  favoritesAvailable: boolean;
  favoritesLimit: number;
  selectedSimulationTracker?: SimulationTracker;
  selectedSimulationTrackerAgentSettings?: SimulationTrackerAgentSettings;
  selectedSimulationTrackerScenarios: SimulationTracker[];
  selectedSimulationTrackerScenariosId: string;
  isSimulationNameAvailable:boolean; 
  simulationName:string;
}

const defaultState: SimulationTrackerStateModel = {
  simulationTrackers: [],
  sessionSimulationTrackers: [],
  selectedSimulationTrackerScenarios: [],
  favoritesAvailable: true,
  favoritesLimit: 3,
  selectedSimulationTrackerScenariosId: '',
  isSimulationNameAvailable:false,
  simulationName:''
};

@State<SimulationTrackerStateModel>({
  name: 'simulationTracker',
  defaults: defaultState,
})
@Injectable()
export class SimulationTrackerState {
  constructor(
    private simulationTrackerService: SimulationTrackerService,
    private apiTerrainService: ApiTerrainService
  ) {}

  @Action(SimulationTrackerActions.CreateSimulationRunGroup)
  createSimulationRunGroup(
    ctx: StateContext<SimulationTrackerStateModel>,
    {
      simulationId,
      simulationName,
      iterations,
      runGroup,
      name,
    }: SimulationTrackerActions.CreateSimulationRunGroup
  ) {
    const requestScenarios: SimulationRunGroupRequest = {
      iterations: iterations,
      simulationName:simulationName,
      scenarios: runGroup,
      fileName: name,
    };

    return this.apiTerrainService
      .queueSimulationRunGroup(simulationId, requestScenarios)
      .pipe(
        catchError((error) => {
          ctx.dispatch(
            new NotificationsActions.Toast(
              'Failed to initiate simulation.',
              Severity.Error
            )
          );
          throw error;
        }),
        tap((newTracker: SimulationTracker[]) => {
          ctx.setState(
            patch({
              simulationTrackers: append([...newTracker]),
              sessionSimulationTrackers: append([...newTracker]),
            })
          );
          asapScheduler.schedule(() => {
            ctx.dispatch(
              new NotificationsActions.Toast(
                `${newTracker.length} simulation${
                  newTracker.length === 1 ? '' : 's'
                } have been initiated successfully.`,
                Severity.Success
              )
            );
            ctx.dispatch(
              new NotificationsActions.Toast(
                'Simulation is running. An email will be sent when results are ready to view.',
                Severity.Success
              )
            );
          });
        })
      );
  }

  @Selector()
  static selectedSimulationTracker(
    state: SimulationTrackerStateModel
  ): SimulationTracker | null {
    return state.selectedSimulationTracker ?? null;
  }

  @Selector()
  static selectedSimulationTrackerAgentSettings(
    state: SimulationTrackerStateModel
  ): AgentSettings | null {
    return state.selectedSimulationTrackerAgentSettings?.agentSettings ?? null;
  }

  @Selector()
  static selectedSimulationTrackerScenarios(
    state: SimulationTrackerStateModel
  ): SimulationTracker[] {
    return state.selectedSimulationTrackerScenarios;
  }

  @Selector()
  static selectedSimulationTrackerScenariosDropdowns(
    state: SimulationTrackerStateModel
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): any[] {
    return state.selectedSimulationTrackerScenarios.map((tracker) => {
      return {
        scenario: tracker.scenario,
        studyAreaId: tracker.studyAreaId,
        simulationTrackerId: tracker.simulationTrackerId,
        simulationId: tracker.simulationId,
        status: tracker.status,
        disabled: tracker.status !== SimulationTrackerStatus.Completed,
      };
    });
  }

  @Action(SimulationTrackerActions.CreateSimulationBatch)
  createSimulationBatch(
    ctx: StateContext<SimulationTrackerStateModel>,
    {
      simulationId,
      configuration,
    }: SimulationTrackerActions.CreateSimulationBatch
  ) {
    return this.apiTerrainService
      .createSimulationBatch(simulationId, configuration)
      .pipe(
        catchError((error) => {
          ctx.dispatch(
            new NotificationsActions.Toast(
              'Failed to initiate simulation.',
              Severity.Error
            )
          );
          throw error;
        }),
        tap((newTracker: SimulationTracker) => {
          ctx.setState(
            patch({
              simulationTrackers: append([newTracker]),
              sessionSimulationTrackers: append([newTracker]),
            })
          );
          asapScheduler.schedule(() => {
            ctx.dispatch(
              new NotificationsActions.Toast(
                'The simulation has been initiated successfully.',
                Severity.Success
              )
            );
            ctx.dispatch(
              new NotificationsActions.Toast(
                'Simulation is running. An email will be sent when results are ready to view.',
                Severity.Success
              )
            );
          });
        })
      );
  }

  @Action(SimulationTrackerActions.GetSimulationTracker)
  getSimulationTracker(
    ctx: StateContext<SimulationTrackerStateModel>,
    action: SimulationTrackerActions.GetSimulationTracker
  ) {
    return this.simulationTrackerService
      .getSimulationTracker(action.simulationTrackerId)
      .pipe(
        tap((simulationTracker: SimulationTracker) => {
          ctx.setState(
            patch({
              simulationTrackers: updateItem<SimulationTracker>(
                (tracker) =>
                  tracker.simulationTrackerId === action.simulationTrackerId,
                simulationTracker
              ),
              sessionSimulationTrackers: updateItem<SimulationTracker>(
                (tracker) =>
                  tracker.simulationTrackerId === action.simulationTrackerId,
                simulationTracker
              ),
              selectedSimulationTrackerScenarios: updateItem<SimulationTracker>(
                (tracker) =>
                  tracker.simulationTrackerId === action.simulationTrackerId,
                simulationTracker
              ),
            })
          );
        })
      );
  }

  @Action(SimulationTrackerActions.GetSimulationTrackersByScnenario, {
    cancelUncompleted: true,
  })
  GetSimulationTrackersByScnenario(
    ctx: StateContext<SimulationTrackerStateModel>,
    { scenarioId }: SimulationTrackerActions.GetSimulationTrackersByScnenario
  ) {
    if (scenarioId == null || scenarioId === '') {
      ctx.setState(
        patch({
          selectedSimulationTrackerScenarios: [],
          selectedSimulationTrackerScenariosId: scenarioId,
        })
      );

      return EMPTY;
    }

    if (ctx.getState().selectedSimulationTrackerScenariosId !== scenarioId) {
      return this.simulationTrackerService
        .getScenarioSimulationTrackers(scenarioId)
        .pipe(
          tap((simulationTrackers: SimulationTracker[]) => {
            simulationTrackers.sort((a, b) => {
              const [valA, valB] = [a.scenario!, b.scenario!];
              const [numA, numB] = [parseFloat(valA), parseFloat(valB)];

              return !isNaN(numA) && !isNaN(numB)
                ? numB - numA
                : valB.localeCompare(valA);
            });

            ctx.setState(
              patch({
                selectedSimulationTrackerScenarios: simulationTrackers,
                selectedSimulationTrackerScenariosId: scenarioId,
              })
            );
          })
        );
    } else {
      return EMPTY;
    }
  }

  @Action(SimulationTrackerActions.SelectSimulationTracker, {
    cancelUncompleted: true,
  })
  selectSimulationTracker(
    ctx: StateContext<SimulationTrackerStateModel>,
    { simulationTrackerId }: SimulationTrackerActions.SelectSimulationTracker
  ) {
    return forkJoin([
      this.simulationTrackerService.getSimulationTracker(simulationTrackerId),
      this.simulationTrackerService.getSimulationTrackerAgentSettings(
        simulationTrackerId
      ),
    ]).pipe(
      tap(([simulationTracker, agentSettings]) => {
        ctx.dispatch(
          new SimulationTrackerActions.GetSimulationTrackersByScnenario(
            simulationTracker.scenarioId
          )
        );
        ctx.setState(
          patch({
            selectedSimulationTracker: simulationTracker,
            selectedSimulationTrackerAgentSettings: {
              agentSettings,
              simulationTrackerId: simulationTracker.simulationTrackerId,
              simulationId: simulationTracker.simulationId,
            },
          })
        );
      })
    );
  }

  @Action(SimulationTrackerActions.GetSimulationTrackers)
  getSimulationTrackers(ctx: StateContext<SimulationTrackerStateModel>) {
    return this.simulationTrackerService.getSimulationTrackers().pipe(
      tap((simulationTrackers: SimulationTracker[]) => {
        simulationTrackers.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));

        const sessionSimulationTrackers: SimulationTracker[] =
          ctx.getState().sessionSimulationTrackers;
        const updatedSessionSimulationTrackers: SimulationTracker[] =
          simulationTrackers.filter((tracker) => {
            return sessionSimulationTrackers.some(
              (t) => t.simulationTrackerId === tracker.simulationTrackerId
            );
          });

        ctx.setState(
          patch({
            simulationTrackers: simulationTrackers,
            sessionSimulationTrackers: updatedSessionSimulationTrackers,
          })
        );
      })
    );
  }

   @Action(SimulationTrackerActions.IsSimulationNameAvailable)
  isSimulationNameAvailable(
    ctx: StateContext<SimulationTrackerStateModel>,
    action: SimulationTrackerActions.IsSimulationNameAvailable
  ) {
    return this.simulationTrackerService
      .isSimulationNameAvailable(action.simulationName)
      .pipe(
        tap((isExists: boolean) => {
          ctx.setState(
            patch({
              isSimulationNameAvailable:!isExists  //if simulation is in use that means not available for use.
            })
          );
        })
      );
  }

  @Selector()
  static isSimulationNameAvailable(
    state: SimulationTrackerStateModel
  ): boolean {
    return state.isSimulationNameAvailable;
  }

  @Selector()
  static simulationTrackers(
    state: SimulationTrackerStateModel
  ): SimulationTracker[] {
    return state.simulationTrackers;
  }

  @Selector()
  static sessionSimulationTrackers(
    state: SimulationTrackerStateModel
  ): SimulationTracker[] {
    return state.sessionSimulationTrackers;
  }

  static getSessionSimulationTrackersByStudyArea(studyAreaId: string) {
    return createSelector(
      [SimulationTrackerState],
      (state: SimulationTrackerStateModel) => {
        return state.sessionSimulationTrackers
          .filter((tracker) => tracker.studyAreaId === studyAreaId)
          .sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
      }
    );
  }

  @Action(SimulationTrackerActions.FavoriteSimulationTracker)
  favoriteSimulationTracker(
    ctx: StateContext<SimulationTrackerStateModel>,
    { simulationTrackerId }: SimulationTrackerActions.FavoriteSimulationTracker
  ) {
    return this.simulationTrackerService
      .favoriteSimulationTracker(simulationTrackerId)
      .pipe(
        tap(() => {
          ctx.setState(
            patch({
              simulationTrackers: updateItem(
                (item) => item.simulationTrackerId === simulationTrackerId,
                patch({ favorited: true })
              ),
            })
          );
        })
      );
  }

  @Action(SimulationTrackerActions.DeleteMultipleSimulationTracker)
  deleteMultipleSimulationTracker(
    ctx: StateContext<SimulationTrackerStateModel>,
    { simulationTrackerIds }: SimulationTrackerActions.DeleteMultipleSimulationTracker
  ) {
    return this.simulationTrackerService
      .deleteMultipleSimulationTracker(simulationTrackerIds)
      .pipe(
        catchError((error) => {
          ctx.dispatch(
            new NotificationsActions.Toast(
              'Failed to delete result.',
              Severity.Error
            )
          );
          throw error;
        }),
        tap(() => {
          ctx.setState(
            patch({
              simulationTrackers: removeItem<SimulationTracker>(
              (item) => simulationTrackerIds.includes(item.simulationTrackerId)
              ),
             sessionSimulationTrackers: removeItem<SimulationTracker>(
              (item) => simulationTrackerIds.includes(item.simulationTrackerId)
              ),
            })
          );

          asapScheduler.schedule(() => {
            ctx.dispatch(
              new NotificationsActions.Toast(
                'The simulation result has been deleted successfully.',
                Severity.Success
              )
            );
          });
        })
      );
  }

  @Action(SimulationTrackerActions.RemoveCompletedSimulationTrackersFromSession)
  removeCompletedSimulationTrackersFromSession(
    ctx: StateContext<SimulationTrackerStateModel>
  ) {
    ctx.setState(
      patch({
        sessionSimulationTrackers: removeItem<SimulationTracker>(
          (item) => item.status === SimulationTrackerStatus.Completed
        ),
      })
    );
  }

  @Action(SimulationTrackerActions.AddLatestSimulationTrackerToSession)
  addLatestSimulationTrackerToSession(
    ctx: StateContext<SimulationTrackerStateModel>
  ) {
    return this.simulationTrackerService.getSimulationTrackers().pipe(
      tap((simulationTrackers: SimulationTracker[]) => {
        simulationTrackers.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));

        ctx.setState(
          patch({
            simulationTrackers: simulationTrackers,
            sessionSimulationTrackers: append([simulationTrackers[0]]),
          })
        );
      })
    );
  }

  @Action(SimulationTrackerActions.RemoveFavoritedSimulationTracker)
  removeFavoritedSimulationTracker(
    ctx: StateContext<SimulationTrackerStateModel>,
    { simulationTrackerId }: SimulationTrackerActions.FavoriteSimulationTracker
  ) {
    return this.simulationTrackerService
      .removeFavoritedSimulationTracker(simulationTrackerId)
      .pipe(
        tap(() => {
          ctx.setState(
            patch({
              simulationTrackers: updateItem(
                (item) => item.simulationTrackerId === simulationTrackerId,
                patch({ favorited: false })
              ),
            })
          );
        })
      );
  }

  @Action(SimulationTrackerActions.GetFavoritedSimulationTrackers)
  getFavoritedSimulationTrackers(
    ctx: StateContext<SimulationTrackerStateModel>
  ) {
    return this.simulationTrackerService.getFavoritedSimulationTrackers().pipe(
      tap((favoritedSimulationTrackers: FavoritedSimulationTrackers) => {
        ctx.setState(
          patch({
            favoritesAvailable: favoritedSimulationTrackers.favoritesAvailable,
            favoritesLimit: favoritedSimulationTrackers.favoritesLimit,
          })
        );
      })
    );
  }

  @Selector()
  static favoritesAvailable(state: SimulationTrackerStateModel): boolean {
    return state.favoritesAvailable;
  }

  @Selector()
  static favoritesLimit(state: SimulationTrackerStateModel): number {
    return state.favoritesLimit;
  }
}
